import { Injectable, Injector } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/**
 * Service responsible for handling HTTP request mocking.
 * Keeps mock logic separate from interceptors while allowing
 * the interceptor to make the decision whether to mock or not.
 */
@Injectable({
  providedIn: 'root'
})
export class HttpMockHandlerService {
  private mockService: any;

  constructor(private injector: Injector) {}

  /**
   * Checks if the request should be mocked
   */
  shouldMock(req: HttpRequest<any>): boolean {
    const url = req.url;

    return url.includes('/audiences/criteria') ||
           url.includes('/facebook/search/targeting') ||
           url.includes('/facebook/search/geographies');
  }

  /**
   * Returns a mock response for the request
   */
  getMockResponse(req: HttpRequest<any>): Observable<any> | null {
    const url = req.url;

    // Mock criteria endpoint
    if (url.includes('/audiences/criteria')) {
      const body = req.body;
      return from(this.loadMockService()).pipe(
        switchMap(service => service.getCriteria(body?.description, body?.conversationId))
      );
    }

    // Mock targeting search endpoint
    if (url.includes('/facebook/search/targeting')) {
      const queryParams = new URL(req.urlWithParams).searchParams;
      const query = queryParams.get('q');
      const type = queryParams.get('type') || 'adinterest';

      if (query) {
        return from(this.loadMockService()).pipe(
          switchMap(service => service.getSuggestions(query, type))
        );
      }
    }

    // Mock geography search endpoint
    if (url.includes('/facebook/search/geographies')) {
      const queryParams = new URL(req.urlWithParams).searchParams;
      const query = queryParams.get('q');

      if (query) {
        return from(this.loadMockService()).pipe(
          switchMap(service => service.searchGeographies(query))
        );
      }
    }

    return null;
  }

  /**
   * Lazy load mock service only when needed
   */
  private async loadMockService() {
    if (!this.mockService) {
      const { AiAudienceGenerationMockService } = await import('./ai-audience-generation/ai-audience-generation-mock.service');
      this.mockService = this.injector.get(AiAudienceGenerationMockService);
    }
    return this.mockService;
  }
}
